import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CourseraStyles,
  ExecutiveSummaryCard,
  SEAExplainerCard,
  APACSplitCard,
  IndiaOverviewCard,
  APACContextCard,
} from "./CourseraCampaignComponents";

import SEAHeatmapCard from "./components/SEAHeatmapCard";
import CampaignTimelineTab from "./tabs/CampaignTimelineTab";
import LeadPacingTab from "./tabs/LeadPacingTab";
import KPIsTab from "./tabs/KPIsTab";
import TitleStrategyTab from "./tabs/TitleStrategyTab";
import AccountsTab from "./tabs/AccountsTab";

function Overlay({ open, onClose, title, children, size = "lg" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background:
          "radial-gradient(80% 100% at 50% 0%, rgba(10,20,40,.65), rgba(10,16,30,.88))",
        backdropFilter: "saturate(115%) blur(6px)",
        zIndex: 50,
        overflow: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "28px 16px",
        }}
      >
        <div
          className="container"
          style={{
            background: "var(--card)",
            borderRadius: 20,
            padding: 20,
            maxWidth: size === "sm" ? 560 : 1152,
          }}
        >
          <div className="section-head" style={{ marginBottom: 14 }}>
            <h2 className="h2" style={{ margin: 0 }}>
              {title}
            </h2>
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [tab, setTab] = useState("overview");
  const [seaOpen, setSeaOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);

  // 1. Centralized Lead Data (Actuals)
  const [actuals, setActuals] = useState([]);

  useEffect(() => {
    fetch("/APAC_Reporting_India_SEA.csv")
      .then((res) => res.text())
      .then((csvText) => {
        const lines = csvText
          .split(/\r?\n/)
          .filter((line) => line.trim() !== "");
        const headers = lines[0]
          .split(",")
          .map((h) => h.trim().replace(/^"|"$/g, ""));
        const data = lines
          .slice(1)
          .map((line) => {
            const values = line
              .split(",")
              .map((v) => v.trim().replace(/^"|"$/g, ""));
            return headers.reduce((obj, header, i) => {
              obj[header] = values[i];
              return obj;
            }, {});
          })
          .filter((row) => row.Date && row.Date.includes("/"));

        setActuals(data);
      });
  }, []);

  // 2. Shared Pacing State
  const [pacing, setPacing] = useState({
    totalLeads: 440,
    phase1Leads: 150,
    phase1Start: "2025-12-08",
    phase1Weeks: 6,
    phase2Start: "2026-01-19",
    phase2Weeks: 6,
    regionSplit: { SEA: 220, INDIA: 220 },
    industryTargets: { FinServ: 220, Tech: 220 },
  });

  const deliveryWindow = useMemo(() => {
    const start = new Date(pacing.phase1Start + "T00:00:00");
    const end = new Date(pacing.phase2Start + "T00:00:00");
    end.setDate(end.getDate() + pacing.phase2Weeks * 7 - 3);
    const fmt = (d) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(start)} – ${fmt(end)}`;
  }, [pacing]);

  return (
    <>
      <CourseraStyles />
      <div className="container">
        <div className="section tabs" style={{ gap: 8, marginTop: 8 }}>
          {["overview", "timeline", "pacing", "titles", "accounts", "kpis"].map(
            (id) => (
              <button
                key={id}
                className={`tab ${tab === id ? "active" : ""}`}
                onClick={() => setTab(id)}
              >
                {id.charAt(0).toUpperCase() + id.slice(1)}
              </button>
            )
          )}
        </div>

        {tab === "overview" && (
          <ExecutiveSummaryCard
            actualCount={actuals.length} // Displaying the 201 leads on the home page
            onOpenSEA={() => setSeaOpen(true)}
            budgetPrivacy={{ mode: "modal", onOpen: () => setBudgetOpen(true) }}
            delivery={{ window: deliveryWindow }}
          />
        )}

        {tab === "pacing" && (
          <LeadPacingTab vars={pacing} actuals={actuals} onChange={setPacing} />
        )}

        {tab === "kpis" && (
          <KPIsTab
            asOf="2026-02-27"
            actualTotal={actuals.length}
            goalTotal={pacing.totalLeads}
            regionGoals={{
              SEA: pacing.regionSplit.SEA,
              India: pacing.regionSplit.INDIA,
            }}
          />
        )}

        {/* Render other tabs normally */}
        {tab === "timeline" && <CampaignTimelineTab />}
        {tab === "titles" && <TitleStrategyTab />}
        {tab === "accounts" && <AccountsTab />}
      </div>

      {/* Overlays remain the same */}
      <Overlay
        open={seaOpen}
        onClose={() => setSeaOpen(false)}
        title="SEA Overview"
      >
        <SEAHeatmapCard data={[]} />{" "}
        {/* Heatmap can now pull from actuals if needed */}
      </Overlay>
    </>
  );
}
